import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import ShoppingListDetail from "../components/ShoppingListDetail";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faRobot, faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useSocket } from "../contexts/SocketContext";

function ShoppingList() {
  const { t } = useTranslation();
  const { socket } = useSocket();
  const [shoppingLists, setShoppingLists] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [itemSuggestions, setItemSuggestions] = useState([]);

  const [newListName, setNewListName] = useState("");
  const [listNameError, setListNameError] = useState(false);
  const [isArchivedOpen, setIsArchivedOpen] = useState(false);

  const fetchShoppingLists = async () => {
    try {
      const response = await axios.get("/api/shopping-lists");
      setShoppingLists(response.data);
    } catch (error) {
      console.error("Error fetching shopping lists:", error);
    }
  };

  const fetchItemSuggestions = async () => {
    try {
      const response = await axios.get("/api/shopping-lists/suggestions/items");
      setItemSuggestions(response.data);
    } catch (error) {
      console.error("Error fetching item suggestions:", error);
    }
  };

  // Handle real-time updates from socket
  const handleSocketUpdate = useCallback((data) => {
    const { listId, list, eventType } = data;
    
    if (eventType === "created") {
      // Add new list to the beginning
      setShoppingLists((prev) => [list, ...prev]);
    } else if (eventType === "deleted") {
      // Remove the deleted list
      setShoppingLists((prev) => prev.filter((l) => l._id !== listId));
    } else {
      // Update existing list (updated, item-added, item-updated, item-deleted)
      setShoppingLists((prev) =>
        prev.map((l) => (l._id === listId ? list : l))
      );
    }
    
    // Refresh suggestions when items change
    if (eventType === "item-added") {
      fetchItemSuggestions();
    }
  }, []);

  useEffect(() => {
    fetchShoppingLists();
    fetchItemSuggestions();
  }, []);

  // Subscribe to socket events for real-time updates
  useEffect(() => {
    if (!socket) return;

    socket.on("shopping-lists:changed", handleSocketUpdate);

    return () => {
      socket.off("shopping-lists:changed", handleSocketUpdate);
    };
  }, [socket, handleSocketUpdate]);

  const handleAddList = async (e) => {
    e.preventDefault();
    if (!newListName.trim()) {
      setListNameError(true);
      setTimeout(() => setListNameError(false), 2000);
      return;
    }
    setListNameError(false);
    try {
      await axios.post("/api/shopping-lists", { name: newListName });
      fetchShoppingLists();
      setNewListName("");
    } catch (error) {
      console.error("Error adding list:", error);
    }
  };

  const handleAiSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post("/api/gemini/format-list", {
        text: aiInput,
      });
      const formattedList = response.data;

      await axios.post("/api/shopping-lists", formattedList);

      fetchShoppingLists();
      setIsModalOpen(false);
      setAiInput("");
      setNewListName("");
    } catch (error) {
      console.error("Failed to format or create shopping list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchiveList = async (listId) => {
    try {
      const response = await axios.put(`/api/shopping-lists/${listId}`, {
        status: "archived",
      });
      setShoppingLists(shoppingLists.map((list) => (list._id === listId ? response.data : list)));
    } catch (error) {
      console.error("Error archiving list:", error);
    }
  };

  const handleUnarchiveList = async (listId) => {
    try {
      const response = await axios.put(`/api/shopping-lists/${listId}`, {
        status: "active",
      });
      setShoppingLists(shoppingLists.map((list) => (list._id === listId ? response.data : list)));
    } catch (error) {
      console.error("Error unarchiving list:", error);
    }
  };

  const handleDeleteList = async (listId) => {
    try {
      await axios.delete(`/api/shopping-lists/${listId}`);
      setShoppingLists(shoppingLists.filter((list) => list._id !== listId));
    } catch (error) {
      console.error("Error deleting list:", error);
    }
  };

  const activeLists = shoppingLists.filter((list) => list.status === "active");

  const archivedLists = shoppingLists.filter((list) => list.status === "archived");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-3xl lg:max-w-5xl mx-auto space-y-6"
    >
      {/* Add New List Form */}
      <form onSubmit={handleAddList} className="relative z-20">
        <div className="flex items-center bg-white dark:bg-dark-surface rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-2 pl-4 transition-all focus-within:ring-2 focus-within:ring-primary-100 focus-within:border-primary-300">
          <input
            type="text"
            value={newListName}
            onChange={(e) => {
              setNewListName(e.target.value);
              if (listNameError) setListNameError(false);
            }}
            placeholder={t("newListName")}
            className={`flex-grow bg-transparent border-none outline-none text-gray-800 dark:text-white placeholder-gray-400 text-lg min-w-0 ${
              listNameError ? "placeholder-red-400" : ""
            }`}
          />

          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            <button
              type="button"
              onClick={() => {
                setAiInput(`Назва списку: ${newListName}\nЕлементи списку:\n`);
                setIsModalOpen(true);
              }}
              className="p-2 text-purple-500 hover:bg-purple-50 rounded-xl transition-colors"
              title="AI Assistant"
            >
              <FontAwesomeIcon icon={faRobot} size="lg" />
            </button>

            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white p-3 rounded-xl shadow-md shadow-primary-200 dark:shadow-none transition-all"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        </div>
      </form>

      {/* Active Lists */}
      <div className="space-y-4">
        <AnimatePresence>
          {activeLists.map((list) => (
            <motion.div
              key={list._id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              layout
              className="card bg-white dark:bg-dark-surface p-2 transition-colors"
            >
              <ShoppingListDetail
                key={list._id}
                list={list}
                setShoppingLists={setShoppingLists}
                handleArchiveList={handleArchiveList}
                itemSuggestions={itemSuggestions}
                onItemAdded={fetchItemSuggestions}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Archived Lists Accordion */}
      {archivedLists.length > 0 && (
        <div className="pt-4 border-t border-gray-100">
          <button
            onClick={() => setIsArchivedOpen(!isArchivedOpen)}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 font-medium w-full py-2"
          >
            <FontAwesomeIcon icon={isArchivedOpen ? faChevronDown : faChevronRight} size="sm" />
            {t("archivedLists")} ({archivedLists.length})
          </button>

          <AnimatePresence>
            {isArchivedOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="space-y-3 pt-2 pb-4">
                  {archivedLists.map((list) => (
                    <div key={list._id} className="bg-gray-50 dark:bg-dark-surface p-4 rounded-xl border border-gray-100 dark:border-gray-700 transition-colors">
                      <ShoppingListDetail
                        key={list._id}
                        list={list}
                        setShoppingLists={setShoppingLists}
                        handleUnarchiveList={handleUnarchiveList}
                        handleDeleteList={handleDeleteList}
                      />
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* AI Modal */}
      {createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setIsModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4 text-purple-600">
                    <FontAwesomeIcon icon={faRobot} size="xl" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">AI Assistant</h3>
                  </div>

                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-8 space-y-4">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                      <p className="text-gray-500 animate-pulse">Generating list...</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">{t("inputListInFreeForm")}</p>
                      <textarea
                        className="w-full h-48 p-4 bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-100 focus:border-purple-400 outline-none resize-none text-gray-700 dark:text-gray-200 transition-colors"
                        placeholder="Example: milk, bread, eggs..."
                        value={aiInput}
                        onChange={(e) => setAiInput(e.target.value)}
                        autoFocus
                      ></textarea>
                      <div className="flex justify-end gap-3 mt-6">
                        <button
                          onClick={() => setIsModalOpen(false)}
                          className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors font-medium"
                        >
                          {t("cancel")}
                        </button>
                        <button
                          onClick={handleAiSubmit}
                          className="px-6 py-2 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white rounded-lg shadow-lg shadow-purple-200 dark:shadow-none transition-all font-medium"
                        >
                          {t("submit")}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </motion.div>
  );
}

export default ShoppingList;
